<template>
    <div
        ref="reference"
        :class="triggerClass"
        @mouseenter="show = true"
        @mouseleave="show = false"
        @focusin="show = true"
        @focusout="show = false"
    >
        <div
            class="block focus:outline-none focus:ring-0 active:ring-0 rounded"
            :role="interactive ? 'button' : null"
            :tabindex="interactive ? 0 : null"
            :aria-haspopup="interactive ? 'true' : null"
            :aria-expanded="interactive ? show : null"
        >
            <slot name="trigger">
                <i :class="`fa-regular ${icon} ml-1`"></i>
            </slot>
        </div>
    </div>
    <Teleport to="body">
        <transition name="ui-tooltip-fade">
            <div
                v-if="show"
                ref="floating"
                :style="floatingStyles"
                class="z-[9999] min-w-44 max-w-xs p-2 rounded shadow-lg bg-[#1c293d] text-white"
                role="tooltip"
            >
                <div class="text-xs whitespace-normal text-center">
                    <slot>{{ text }}</slot>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import {
    useFloating,
    autoUpdate,
    offset as offsetMiddleware,
    flip,
    shift,
} from '@floating-ui/vue';
import { useAppStore } from '@/store/app';

const props = defineProps({
    text: {
        type: String,
        default: '',
    },
    placement: {
        type: String,
        default: 'bottom',
    },
    offset: {
        type: Number,
        default: 6,
    },
    icon: {
        type: String,
        default: 'fa-circle-exclamation',
    },
    // Set to false when the tooltip wraps an already-interactive element
    // (e.g. a real <button>) or a deliberately disabled control. Removes the
    // role="button"/tabindex="0" from the inner div so the area doesn't
    // appear as an extra focusable, "clickable" target.
    interactive: {
        type: Boolean,
        default: true,
    },
    // Tailwind classes applied to the trigger wrapper. Override when the
    // tooltip needs to fill its parent (e.g. wrapping a truncated element
    // that depends on a constrained width from a flex/grid parent).
    triggerClass: {
        type: String,
        default: 'inline-block',
    },
});

const show = ref(false);
const reference = ref(null);
const floating = ref(null);

const { floatingStyles, update } = useFloating(reference, floating, {
    strategy: 'fixed',
    placement: props.placement,
    whileElementsMounted: autoUpdate,
    middleware: [
        offsetMiddleware(props.offset),
        flip({ padding: 8 }),
        shift({ padding: 8 }),
    ],
});

const appStore = useAppStore();
const { sidebarOpen } = storeToRefs(appStore);

watch(sidebarOpen, () => {
    if (show.value) {
        setTimeout(() => {
            nextTick(() => update());
        }, 250);
    }
});
</script>

<style scoped>
.ui-tooltip-fade-enter-active,
.ui-tooltip-fade-leave-active {
    transition: opacity 0.15s ease;
}
.ui-tooltip-fade-enter-from,
.ui-tooltip-fade-leave-to {
    opacity: 0;
}
</style>
